// The sample plan: which samples a new purchase order asks the supplier for (lab dip, Initial Sample, Fit Sample, PPS)
// and how many days the supplier has for each. The organisation sets a default (and, if it wants, one per garment
// category) in Settings → Samples, Gold Seal & STP; the person raising a PO starts from it and can change it for that
// order (days, due dates, untick what isn't needed, per line, or no samples). When the PO is issued, the lab dip and the
// first sample are requested and the supplier (and the agent, when samples go through the agent) get one email; each
// later sample is requested when the one before is approved. Also: where a sample is on its way (supplier → agent →
// buyer). Shared by the server (which enforces it) and the screens.

export const PLAN_STAGES = ["labdip", "sample", "fit", "pps"] as const;
export type PlanStage = (typeof PLAN_STAGES)[number];
export const SAMPLE_SEQUENCE = ["sample", "fit", "pps"] as const;
export type SequenceStage = (typeof SAMPLE_SEQUENCE)[number];
export const PLAN_STAGE_LABEL: Record<PlanStage, string> = { labdip: "Lab dip", sample: "Initial Sample", fit: "Fit Sample", pps: "PPS" };

export type Needs = Record<PlanStage, boolean>;
export type Days = Record<PlanStage, number>;
export interface StagePlan { needs: Needs; days: Days }
export interface CategoryPlan extends StagePlan { category: string }
export interface SamplePlanSettings {
  /** Email the supplier (and agent) when samples are requested, and a daily reminder while one is late. */
  email: boolean;
  base: StagePlan;
  /** Plans for particular garment categories (e.g. T-shirts, Jackets); the base plan is used for everything else. */
  categories: CategoryPlan[];
}
export const DEFAULT_DAYS: Days = { labdip: 7, sample: 14, fit: 10, pps: 10 };
export const ALL_NEEDED: Needs = { labdip: true, sample: true, fit: true, pps: true };
export const DEFAULT_SAMPLE_PLAN: SamplePlanSettings = { email: true, base: { needs: { ...ALL_NEEDED }, days: { ...DEFAULT_DAYS } }, categories: [] };

const day = (v: unknown, d: number) => { const n = Math.round(Number(v)); return Number.isFinite(n) && n >= 0 && n <= 365 ? n : d; };
export function normalizeNeeds(v: any, base: Needs = ALL_NEEDED): Needs {
  return Object.fromEntries(PLAN_STAGES.map((k) => [k, typeof v?.[k] === "boolean" ? v[k] : base[k]])) as Needs;
}
export function normalizeDays(v: any, base: Days = DEFAULT_DAYS): Days {
  return Object.fromEntries(PLAN_STAGES.map((k) => [k, day(v?.[k], base[k])])) as Days;
}
export function normalizeStagePlan(v: any, base: StagePlan = DEFAULT_SAMPLE_PLAN.base): StagePlan {
  return { needs: normalizeNeeds(v?.needs, base.needs), days: normalizeDays(v?.days, base.days) };
}
export function normalizeSamplePlanSettings(v: any): SamplePlanSettings {
  const base = normalizeStagePlan(v?.base);
  const seen = new Set<string>();
  const categories: CategoryPlan[] = [];
  for (const c of Array.isArray(v?.categories) ? v.categories : []) {
    const category = String(c?.category ?? "").trim().slice(0, 80);
    if (!category || seen.has(category.toLowerCase())) continue;
    seen.add(category.toLowerCase());
    categories.push({ category, ...normalizeStagePlan(c, base) });
    if (categories.length >= 40) break;
  }
  return { email: v?.email !== false, base, categories };
}
/** The plan for a garment category (the base plan when it has none of its own). */
export function planForCategory(s: SamplePlanSettings, category: string | null | undefined): StagePlan {
  const c = String(category || "").trim().toLowerCase();
  const hit = c ? s.categories.find((x) => x.category.toLowerCase() === c) : undefined;
  return hit ? { needs: { ...hit.needs }, days: { ...hit.days } } : { needs: { ...s.base.needs }, days: { ...s.base.days } };
}

// ---- The plan saved on one purchase order ------------------------------------------------------------------------------
export type PlanMode = "all" | "perLine" | "none";
export const PLAN_MODES: Array<[PlanMode, string]> = [["all", "Same for all lines"], ["perLine", "Choose per line"], ["none", "No samples needed (repeat order / STP)"]];
export interface PoSamplePlan {
  mode: PlanMode;
  needs: Needs;
  days: Days;
  /** Due dates typed for this order (yyyy-mm-dd), for the samples requested when the PO is issued. */
  due: Partial<Record<PlanStage, string>>;
}
const ISO = /^\d{4}-\d{2}-\d{2}$/;
export function normalizePoSamplePlan(v: any): PoSamplePlan | null {
  if (!v || typeof v !== "object") return null;
  const mode: PlanMode = (["all", "perLine", "none"] as const).includes(v.mode) ? v.mode : "all";
  const due: Partial<Record<PlanStage, string>> = {};
  for (const k of PLAN_STAGES) if (typeof v.due?.[k] === "string" && ISO.test(v.due[k])) due[k] = v.due[k];
  return { mode, needs: normalizeNeeds(v.needs), days: normalizeDays(v.days), due };
}
/** What one PO line needs: nothing for trims, only the lab dip for fabrics, the plan (or the line's own choice) for garments. */
export function lineNeeds(plan: PoSamplePlan | null, own: unknown, itemType: string): Needs {
  const none: Needs = { labdip: false, sample: false, fit: false, pps: false };
  if (!plan || plan.mode === "none" || itemType === "trim") return none;
  // Per line: the line's own ticks, within what the PO's plan asks for.
  const mine = plan.mode === "perLine" && own && typeof own === "object" ? normalizeNeeds(own, plan.needs) : plan.needs;
  const n = Object.fromEntries(PLAN_STAGES.map((k) => [k, plan.needs[k] && mine[k]])) as Needs;
  if (itemType === "fabric") return { ...none, labdip: n.labdip };
  return { ...n };
}
/** The samples requested when the PO is issued: the lab dip, and the first sample of the sequence that is needed. */
export function stagesAtIssue(n: Needs): PlanStage[] {
  const first = SAMPLE_SEQUENCE.find((k) => n[k]);
  return [...(n.labdip ? ["labdip" as const] : []), ...(first ? [first] : [])];
}
/** The sample requested after `approved` is approved: the next one in the sequence that is needed. */
export function nextAfter(n: Needs, approved: SequenceStage): SequenceStage | null {
  const i = SAMPLE_SEQUENCE.indexOf(approved);
  return SAMPLE_SEQUENCE.slice(i + 1).find((k) => n[k]) ?? null;
}
/** When does a requested sample start: on issue, or after the one before it is approved. */
export function startsText(n: Needs, stage: PlanStage): string {
  if (!n[stage]) return "Not needed";
  if (stagesAtIssue(n).includes(stage)) return "When the PO is issued to the supplier";
  const i = SAMPLE_SEQUENCE.indexOf(stage as SequenceStage);
  const before = SAMPLE_SEQUENCE.slice(0, i).reverse().find((k) => n[k]);
  return before ? `After ${PLAN_STAGE_LABEL[before]} is approved` : "When the PO is issued to the supplier";
}
export function addPlanDays(date: string, days: number): string {
  const t = new Date(`${date}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() + days);
  return t.toISOString().slice(0, 10);
}
/** The due date of a sample requested today: the date typed on the PO (on issue only), else today + its days. */
export function dueFor(plan: PoSamplePlan, stage: PlanStage, today: string, atIssue: boolean): string {
  return (atIssue && plan.due[stage]) || addPlanDays(today, plan.days[stage]);
}

// ---- Where a sample is ---------------------------------------------------------------------------------------------------
export type Where = "with_supplier" | "to_agent" | "at_agent" | "to_you" | "received" | "reviewed";
export const WHERE_LABEL: Record<Where, string> = {
  with_supplier: "With supplier", to_agent: "On the way to the agent", at_agent: "At agent", to_you: "On the way to you", received: "Received", reviewed: "Reviewed",
};
export interface DispatchFacts { status: string; viaAgent?: boolean; agentReceivedDate?: string | null; agentSentDate?: string | null }
export function whereOf(r: DispatchFacts): Where {
  if (["approved", "rejected"].includes(r.status)) return "reviewed";
  if (["received", "under_review"].includes(r.status)) return "received";
  if (r.status === "waiting") return "with_supplier";
  if (!r.viaAgent) return "to_you";
  if (r.agentSentDate) return "to_you";
  return r.agentReceivedDate ? "at_agent" : "to_agent";
}
/** Samples going to different places (the agent / straight to the buyer) can't travel in one parcel. */
export const PARCEL_MIXED = "Samples going to different places can't be in the same parcel. Send the ones going to the agent and the ones coming straight to the buyer separately.";
